import { useRender } from '@base-ui/react/use-render';
import { linkOptions } from '@tanstack/react-router';
import clsx from 'clsx';
import { useMemo } from 'react';
import { PiArrowUpRight, PiGlobe } from 'react-icons/pi';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDialog, useTailwindBreakpoint } from '@_/hooks';
import { Locales } from '@_/static';
import { Link, Text } from '@_/ui';
import { SelectLocaleDialog } from '#src/components/dialogs/SelectLocaleDialog.tsx';
import { Logo } from '#src/components/Logo/index.ts';
import { MAX_PAGE_WIDTH } from '#src/theme/constants.ts';

const navItems = [
  {
    label: <FormattedMessage id="nav.privacy-policy.short" defaultMessage="Privacy" />,
    link: linkOptions({
      from: '/{-$locale}',
      to: '/{-$locale}/privacy-policy'
    })
  },
  {
    label: <FormattedMessage id="nav.terms-of-use.short" defaultMessage="Terms" />,
    link: linkOptions({
      from: '/{-$locale}',
      to: '/{-$locale}/terms'
    })
  }
] as const;

export function AppFooter({
  dark = false,
  showBusinessLandingLink = true,
  simple = false
}: {
  dark?: boolean;
  showBusinessLandingLink?: boolean;
  simple?: boolean;
}) {
  const { locale } = useIntl();
  const isDesktop = useTailwindBreakpoint('md');

  const localeDialog = useDialog({
    component: SelectLocaleDialog
  });

  const year = useMemo(() => new Date().getFullYear(), []);

  return (
    <>
      {localeDialog.element}

      <footer className={clsx(dark && 'bg-gray-950 text-white', 'mt-12 sm:mt-16')}>
        <div className={clsx(MAX_PAGE_WIDTH, 'mx-auto px-4 sm:px-6 lg:px-8')}>
          {!simple && (
            <div className="hidden sm:block sm:gap-x-8 sm:pt-8 sm:pb-6">
              <div className="flex items-center justify-between">
                <Logo dark={dark} outline={dark} />

                <ul className="flex items-center gap-x-6">
                  {navItems.map(item => (
                    <li key={item.link.to}>
                      <Link {...item.link} plain>
                        {item.label}
                      </Link>
                    </li>
                  ))}

                  {isDesktop && (
                    <li>
                      <FooterButton dark={dark} onClick={() => localeDialog.open()} subtleOnDesktop>
                        <PiGlobe className="mr-2 size-5" />
                        <span>{Locales.find(loc => loc.code === locale)?.name}</span>
                      </FooterButton>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}
          <div
            className={clsx(
              'flex flex-col items-center justify-between gap-y-4 py-6 sm:flex-row',
              dark ? 'border-gray-500' : 'border-gray-200',
              dark ? clsx(!simple && 'border-t') : clsx('border-t')
            )}
          >
            <div className="flex gap-x-6 sm:order-2">
              {isDesktop && showBusinessLandingLink && (
                <FooterButton
                  dark={dark}
                  render={<Link from="/{-$locale}" to="/{-$locale}/business" plain />}
                >
                  <span>
                    <FormattedMessage
                      id="nav.use-slashhow-for-your-business"
                      defaultMessage="Use Slash How for your business"
                    />
                  </span>
                  <PiArrowUpRight className="ml-1 size-4" />
                </FooterButton>
              )}

              {(simple || !isDesktop) && (
                <FooterButton dark={dark} onClick={() => localeDialog.open()} subtleOnDesktop>
                  <PiGlobe className="mr-2 size-5" />
                  <span>{Locales.find(loc => loc.code === locale)?.name}</span>
                </FooterButton>
              )}
            </div>
            <div className="sm:order-1">
              <Text dark={dark}>&copy; {year} Slash How Limited</Text>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

function FooterButton({
  children,
  className,
  dark = false,
  render,
  subtleOnDesktop = false,
  ...rest
}: useRender.ComponentProps<'button'> & { dark?: boolean; subtleOnDesktop?: boolean }) {
  return useRender({
    defaultTagName: 'button',
    render,
    props: {
      ...rest,
      children,
      className: clsx(
        'flex items-center rounded-full px-4 py-2 font-semibold',
        subtleOnDesktop ? 'sm:rounded-none sm:bg-transparent sm:p-0' : 'sm:-my-0.5',
        dark ? 'bg-white/10 text-white' : 'bg-black/5 text-gray-900',
        className
      )
    }
  });
}
